import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  TwitterAuthProvider,
  linkWithCredential,
  User as FirebaseUser,
  UserCredential,
  OAuthCredential,
} from 'firebase/auth';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import { User } from '../types/content';

export interface TwitterAuthData {
  twitterId: string;
  twitterUsername: string;
  twitterDisplayName: string;
  twitterProfileImageUrl?: string;
  email?: string;
}

/**
 * The two outcomes of a Twitter sign-in attempt. On desktop the popup resolves in place with
 * a user (`TwitterSignInSuccess`). On mobile / embedded browsers the popup is often blocked or
 * unsupported, so we fall back to a FULL-PAGE redirect: no user resolves on THIS page load —
 * the sentinel `SIGN_IN_REDIRECTING` says "we've navigated away; sign-in completes on the
 * return load via getRedirectSignInResult()". Callers must handle the sentinel instead of
 * treating it as a signed-in (or failed) user.
 */
export interface TwitterSignInSuccess {
  firebaseUser: FirebaseUser;
  twitterData: TwitterAuthData;
  credential: OAuthCredential | null;
}

export const SIGN_IN_REDIRECTING = 'redirecting' as const;

export type TwitterSignInResult = TwitterSignInSuccess | typeof SIGN_IN_REDIRECTING;

/**
 * Firebase error codes that mean "this browser can't do a popup", not "the user cancelled".
 * Only these trigger the redirect fallback — cancellations (`auth/popup-closed-by-user`,
 * `auth/cancelled-popup-request`) are the user's choice and must surface as today.
 */
const ENVIRONMENTAL_POPUP_FAILURE_CODES = new Set([
  'auth/popup-blocked', // browser blocked the popup window
  'auth/operation-not-supported-in-this-environment', // e.g. in-app / embedded webview
  'auth/web-storage-unsupported', // popup needs web storage the environment denies
]);

/**
 * Should this environment use redirect sign-in FIRST (skipping the popup attempt)?
 *
 * On mobile / touch browsers the popup path is unreliable — worse, on Safari/iOS a storage-
 * partitioning failure throws NO recognizable code, so the code-triggered A1 fallback above
 * never fires and the user is silently stranded. Detecting mobile up front and going straight
 * to signInWithRedirect is the robust path there. Desktop keeps the popup (nicer UX, and the
 * A1 code-fallback still covers popup-blockers).
 *
 * Exported for unit tests; `navigator` is injectable (defaults to window.navigator). Guards
 * SSR (no window) → false.
 */
export function shouldUseRedirectSignIn(nav?: Navigator): boolean {
  const navigatorRef =
    nav ?? (typeof window !== 'undefined' ? window.navigator : undefined);
  if (!navigatorRef) {
    return false;
  }

  const ua = navigatorRef.userAgent || '';
  if (/iPhone|iPad|iPod|Android/i.test(ua)) {
    return true;
  }

  // iPadOS ≥13 reports a desktop (MacIntel) UA; detect it via the touch-points tell.
  if (navigatorRef.platform === 'MacIntel' && navigatorRef.maxTouchPoints > 1) {
    return true;
  }

  return false;
}

export class TwitterAuthService {
  private static instance: TwitterAuthService;

  static getInstance(): TwitterAuthService {
    if (!TwitterAuthService.instance) {
      TwitterAuthService.instance = new TwitterAuthService();
    }
    return TwitterAuthService.instance;
  }

  private getAuth() {
    if (!auth) {
      throw new Error('Auth not initialized');
    }
    return auth;
  }

  private getDb() {
    if (!db) {
      throw new Error('Database not initialized');
    }
    return db;
  }

  /**
   * Sign in with Twitter OAuth.
   *
   * Tries `signInWithPopup` first. If the popup can't run in this environment (mobile / in-app
   * browsers frequently block or don't support it), falls back to a full-page
   * `signInWithRedirect` and returns `SIGN_IN_REDIRECTING` — the page is navigating away and no
   * user resolves here; sign-in finishes on the return load (see `getRedirectSignInResult`).
   * User cancellations are NOT redirected — they throw the same friendly errors as before.
   */
  async signInWithTwitter(): Promise<TwitterSignInResult> {
    // Declared outside the try so the catch can reuse them for the redirect fallback.
    const firebaseAuth = this.getAuth();
    const provider = new TwitterAuthProvider();

    // Redirect-FIRST on mobile / touch browsers. The popup path is unreliable there and, on
    // Safari/iOS, fails with no recognizable error code — so the code-triggered fallback below
    // never fires. Going straight to the full-page redirect is the robust mobile path; the page
    // navigates away and sign-in finishes on the return load (getRedirectSignInResult).
    if (shouldUseRedirectSignIn()) {
      try {
        await signInWithRedirect(firebaseAuth, provider);
        // Reached only if the browser hasn't navigated away yet; the redirect is in flight.
        return SIGN_IN_REDIRECTING;
      } catch (redirectError) {
        console.error('Twitter redirect sign-in failed:', redirectError);
        throw new Error('Failed to sign in with Twitter. Please try again.');
      }
    }

    try {
      // Request additional permissions if needed
      // provider.addScope('tweet.read');
      // provider.addScope('users.read');

      const result = await signInWithPopup(firebaseAuth, provider);
      const credential = TwitterAuthProvider.credentialFromResult(result);

      // Extract Twitter data from the result (includes API call for username)
      const twitterData = await this.extractTwitterData(result);

      return {
        firebaseUser: result.user,
        twitterData,
        credential,
      };
    } catch (error: any) {
      // Mobile-resilience fallback: when the popup can't run in this environment (blocked /
      // unsupported), do a full-page redirect instead so contributors arriving from a tweet on
      // their phone can still sign in. NOT for user cancellations — those fall through below.
      if (ENVIRONMENTAL_POPUP_FAILURE_CODES.has(error?.code)) {
        try {
          await signInWithRedirect(firebaseAuth, provider);
          // Reached only if the browser hasn't navigated away yet; the redirect is in flight.
          return SIGN_IN_REDIRECTING;
        } catch (redirectError) {
          console.error('Twitter redirect fallback failed:', redirectError);
          throw new Error('Failed to sign in with Twitter. Please try again.');
        }
      }

      // Handle specific (non-environmental) error cases — surface as today.
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in cancelled. Please try again.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        throw new Error('Another sign-in is in progress. Please wait.');
      }

      console.error('Twitter sign-in error:', error);
      throw new Error('Failed to sign in with Twitter. Please try again.');
    }
  }

  /**
   * Complete a redirect-based Twitter sign-in on page load.
   *
   * Reads Firebase's pending redirect result. Returns the resolved Twitter user in the same
   * shape as the popup success path, or `null` when there is no pending redirect (the common
   * case on a normal page load). Call this where auth initializes (the AuthProvider) so a
   * redirect round-trip finishes sign-in — including CREATING the Firestore user doc for a
   * brand-new contributor, which a bare `onAuthStateChanged` read would miss.
   */
  async getRedirectSignInResult(): Promise<TwitterSignInSuccess | null> {
    const firebaseAuth = this.getAuth();
    const result = await getRedirectResult(firebaseAuth);
    if (!result) {
      return null;
    }

    const credential = TwitterAuthProvider.credentialFromResult(result);
    const twitterData = await this.extractTwitterData(result);

    return {
      firebaseUser: result.user,
      twitterData,
      credential,
    };
  }

  /**
   * Extract Twitter user data from OAuth result
   * Uses Firebase Auth provider data (no external API calls needed)
   */
  private async extractTwitterData(result: UserCredential): Promise<TwitterAuthData> {
    const user = result.user;
    
    // Get additional user info from provider data
    const providerData = user.providerData.find(
      (provider) => provider.providerId === 'twitter.com'
    );
    
    if (!providerData) {
      // Fallback if no provider data found
      return {
        twitterId: user.uid,
        twitterUsername: user.uid,
        twitterDisplayName: user.displayName || 'Twitter User',
        twitterProfileImageUrl: user.photoURL || undefined,
        email: user.email || undefined,
      };
    }
    
    const twitterId = providerData.uid; // Numeric Twitter user ID
    const displayName = providerData.displayName || user.displayName || '';
    const photoURL = providerData.photoURL || user.photoURL || '';
    
    // Firebase Auth provides the Twitter screen name (username) in the displayName field
    // Extract it if it's in the format "Display Name (@username)" or use the numeric ID as fallback
    let twitterUsername = twitterId; // Fallback to numeric ID
    
    // Try to extract username from displayName if it contains @username pattern
    if (displayName) {
      const usernameMatch = displayName.match(/@(\w+)/);
      if (usernameMatch && usernameMatch[1]) {
        twitterUsername = usernameMatch[1]; // Extract the @username without the @
      }
    }
    
    // Additional check: sometimes the username is in the providerData.displayName without @
    // If we still have numeric ID as username, check if displayName looks like a username (no spaces)
    if (twitterUsername === twitterId && displayName && !/\s/.test(displayName) && displayName.length < 16) {
      // Twitter usernames are max 15 chars and no spaces, this might be the username
      twitterUsername = displayName.toLowerCase();
    }
    
    return {
      twitterId: twitterId,
      twitterUsername: twitterUsername,
      twitterDisplayName: displayName,
      twitterProfileImageUrl: photoURL,
      email: user.email || undefined,
    };
  }

  /**
   * Link an existing user account to Twitter
   * Preserves the existing user document ID
   */
  async linkExistingUserToTwitter(existingUserId: string): Promise<TwitterAuthData> {
    try {
      const firebaseAuth = this.getAuth();
      const currentUser = firebaseAuth.currentUser;
      
      if (!currentUser) {
        throw new Error('No user is currently signed in');
      }
      
      // Verify the current user matches the user we want to link
      if (currentUser.uid !== existingUserId) {
        throw new Error('User ID mismatch. Please sign in with the correct account first.');
      }
      
      // Check if user already has Twitter linked
      const hasTwitter = currentUser.providerData.some(
        (provider) => provider.providerId === 'twitter.com'
      );
      
      if (hasTwitter) {
        throw new Error('This account is already linked to Twitter');
      }
      
      // Launch Twitter OAuth to get credentials
      const provider = new TwitterAuthProvider();
      const result = await signInWithPopup(firebaseAuth, provider);
      const twitterData = await this.extractTwitterData(result);
      
      // Update Firestore user document with Twitter data
      await this.updateUserWithTwitterData(existingUserId, twitterData);
      
      return twitterData;
    } catch (error: any) {
      console.error('Error linking Twitter account:', error);
      
      if (error.code === 'auth/credential-already-in-use') {
        throw new Error('This Twitter account is already linked to another user.');
      }
      
      throw error;
    }
  }

  /**
   * Update user document in Firestore with Twitter authentication data
   */
  async updateUserWithTwitterData(
    userId: string,
    twitterData: TwitterAuthData
  ): Promise<void> {
    try {
      const database = this.getDb();
      const userDocRef = doc(database, 'users', userId);
      
      // Verify user exists
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }
      
      // Update with Twitter data
      await updateDoc(userDocRef, {
        authProvider: 'twitter',
        twitterId: twitterData.twitterId,
        twitterUsername: twitterData.twitterUsername,
        twitterDisplayName: twitterData.twitterDisplayName,
        twitterProfileImageUrl: twitterData.twitterProfileImageUrl || null,
        // Update email if provided by Twitter and not already set
        ...(twitterData.email && !userDoc.data().email && { email: twitterData.email }),
        // Update profile image if not already set
        ...(twitterData.twitterProfileImageUrl && 
            !userDoc.data().profileImage && 
            { profileImage: twitterData.twitterProfileImageUrl }),
        lastLoginAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating user with Twitter data:', error);
      throw new Error('Failed to update user profile with Twitter data');
    }
  }

  /**
   * Get Twitter user data from Firestore user document
   */
  async getTwitterUserData(userId: string): Promise<TwitterAuthData | null> {
    try {
      const database = this.getDb();
      const userDoc = await getDoc(doc(database, 'users', userId));
      
      if (!userDoc.exists()) {
        return null;
      }
      
      const data = userDoc.data();
      
      if (!data.twitterId) {
        return null;
      }
      
      return {
        twitterId: data.twitterId,
        twitterUsername: data.twitterUsername || '',
        twitterDisplayName: data.twitterDisplayName || data.displayName,
        twitterProfileImageUrl: data.twitterProfileImageUrl,
        email: data.email,
      };
    } catch (error) {
      console.error('Error getting Twitter user data:', error);
      return null;
    }
  }

  /**
   * Check if a user has Twitter authentication enabled
   */
  async hasTwitterAuth(userId: string): Promise<boolean> {
    const twitterData = await this.getTwitterUserData(userId);
    return twitterData !== null;
  }

  /**
   * Get Twitter profile URL from username
   */
  getTwitterProfileUrl(username: string): string {
    // Remove @ if present
    const cleanUsername = username.startsWith('@') ? username.slice(1) : username;
    return `https://twitter.com/${cleanUsername}`;
  }
}

// Export singleton instance
export const twitterAuthService = TwitterAuthService.getInstance();

